//
// Filters of the query builder (operator, 14.10.2026: "A–E alle ja").
//
// Field and operator come from a list; the value is the one thing typed, and
// it is checked before the query is stored — in the screen, at its row, and
// again on the server. Conditions stand in groups: "all" (and) or "at least
// one" (or); the groups are joined with and. The older plain list of filters
// reads as one "all" group.
//
// No imports: client and server both use it.
//

export type TFilter = { field: string; operator: string; value: string }
export type TFilterMode = 'all' | 'any'
export type TFilterGroup = { mode: TFilterMode; filters: TFilter[] }
export type TFilterRefusal = { group: number; index: number; message: string }

/** What can be chosen, in the order the list shows it. */
export const FILTER_OPERATORS = ['=', '!=', 'IN', 'NOT IN', 'CONTAINS', '>', '>=', '<', '<=', 'IS EMPTY', 'IS NOT EMPTY'] as const

/** Operators that need no value. */
export const NO_VALUE_OPERATORS: readonly string[] = ['IS EMPTY', 'IS NOT EMPTY']
/** Operators that compare numbers; the data is text and is converted for it. */
export const NUMBER_OPERATORS: readonly string[] = ['>', '>=', '<', '<=']
/** Operators whose value is a list, separated by commas. */
export const LIST_OPERATORS: readonly string[] = ['IN', 'NOT IN']

/** The groups of a query; the older plain list is one "all" group. */
export function filterGroupsOf(query: { filters?: TFilter[]; filterGroups?: TFilterGroup[] } | null | undefined): TFilterGroup[] {
    if (query?.filterGroups?.length) return query.filterGroups
    return query?.filters?.length ? [{ mode: 'all', filters: query.filters }] : []
}

/** A number as typed — comma or point as the decimal sign —, or null. */
export function parseFilterNumber(value: string): number | null {
    const text = String(value ?? '').trim()
    return /^-?\d+(?:[.,]\d+)?$/.test(text) ? Number(text.replace(',', '.')) : null
}

/** The parts of a list value; empty parts stay, for the check to find them. */
export const filterListValues = (value: string) => String(value ?? '').split(',').map((part) => part.trim())

/** The name a join is addressed by — "Alias.Field" —, the table name unless one is given. */
export const joinAlias = (join: { as?: string; from?: string }) => String(join.as || join.from || '').trim()

/** Whether a field can be resolved: an own field (with or without the table name) or "Alias.Field" of a join. */
function knownField(field: string, tableName: string, ownFields: string[], aliases: Set<string>) {
    if (ownFields.includes(field)) return true
    if (field.startsWith(`${tableName}.`) && ownFields.includes(field.slice(tableName.length + 1))) return true
    const dot = field.indexOf('.')
    return dot > 0 && aliases.has(field.slice(0, dot)) && field.length > dot + 1
}

/**
 * Every row that cannot be stored as it is, with the sentence to show at it —
 * a key of the language files. Empty when the filters can be stored.
 * `ownFields` null: the converter has no data list yet, so its fields cannot be
 * told — the field is then not checked, the rest is.
 */
export function filterRefusals(
    query: { joins?: { as?: string; from?: string }[]; filters?: TFilter[]; filterGroups?: TFilterGroup[] },
    tableName: string,
    ownFields: string[] | null
): TFilterRefusal[] {
    const aliases = new Set((query?.joins ?? []).map(joinAlias).filter(Boolean))
    const refusals: TFilterRefusal[] = []
    filterGroupsOf(query).forEach((group, groupIndex) => {
        group.filters.forEach((filter, index) => {
            const message = filterRowRefusal(filter, tableName, ownFields, aliases)
            if (message) refusals.push({ group: groupIndex, index, message })
        })
    })
    return refusals
}

function filterRowRefusal(filter: TFilter, tableName: string, ownFields: string[] | null, aliases: Set<string>): string | null {
    const field = String(filter?.field ?? '').trim()
    if (!field || (ownFields && !knownField(field, tableName, ownFields, aliases))) return 'Choose a field from the list.'
    const operator = String(filter?.operator ?? '')
    if (!(FILTER_OPERATORS as readonly string[]).includes(operator)) return 'Choose an operator from the list.'
    if (NO_VALUE_OPERATORS.includes(operator)) return null

    const value = String(filter?.value ?? '').trim()
    if (!value) return 'Enter a value.'
    if (NUMBER_OPERATORS.includes(operator) && parseFilterNumber(value) === null) return 'Enter a number, e.g. 10 or 2,5.'
    if (LIST_OPERATORS.includes(operator) && filterListValues(value).some((part) => !part)) {
        return 'Separate the values with commas, without empty entries.'
    }
    return null
}

/**
 * Why a query cannot be stored, if it cannot: an alias addresses one join
 * only, holds no dot ("Alias.Field" is split there), and is not the name of
 * the converted table. Answers a sentence and the alias for req.t.
 */
export function queryRefusal(query: { joins?: { as?: string; from?: string }[]; [key: string]: unknown }, tableName: string): { message: string; alias: string } | null {
    const seen = new Set<string>()
    for (const join of query?.joins ?? []) {
        const alias = joinAlias(join)
        if (!alias) continue
        if (alias.includes('.')) return { message: 'An alias must not contain a dot: "{{alias}}".', alias }
        if (alias === tableName) return { message: 'The alias "{{alias}}" is the name of the converted table.', alias }
        if (seen.has(alias)) return { message: 'Each join needs an alias of its own: "{{alias}}" is used twice.', alias }
        seen.add(alias)
    }
    return null
}
